import { resolveTourTargetSelector } from './learnerTourRegistry';
import { dispatchTourTargetFocus } from './tourTargetEvents';

/** Wait for collapsible expand + layout after tour focus. */
export const TOUR_TARGET_RESOLVE_MS = 480;

/** Fast skip when a step target is absent (optional steps). */
export const TOUR_QUICK_SKIP_MS = 320;

/** Longer wait when a tip icon may mount asynchronously. */
export const TOUR_TIP_PATIENT_MS = 1200;

const TARGET_RESOLVE_POLL_MS = 150;

/** Targets whose DOM may appear after async layout (inline tip icons). */
export const ASYNC_TIP_TOUR_TARGETS = new Set(['book-tip', 'lesson-tip', 'tag-content-tip']);

const WORD_HELP_SCOPE_TOUR_TARGETS = new Set([
  'lesson-word-help-scope',
  'tag-content-word-help-scope',
  'book-word-help-scope',
]);

/** Corner stack needs time to expand before the close, Leitner, Telegram, and AI steps. */
const EDGE_STACK_TOUR_TARGETS = new Set([
  'edge-actions-close',
  'header-leitner',
  'edge-telegram',
  'header-chatbot',
  'edge-actions-open',
]);

/** Placement wizard pages slide in after the tour focuses them. */
const PLACEMENT_PAGE_TOUR_TARGETS = new Set([
  'knowledge-placement-intro',
  'knowledge-placement-choose-course',
  'knowledge-placement-courses',
  'knowledge-placement-quiz',
  'knowledge-placement-take-quiz',
  'knowledge-placement-skip',
  'knowledge-placement-change-course',
]);

/** Targets that may need page navigation or layout before the element is measurable. */
export const PATIENT_FIND_TOUR_TARGETS = new Set([
  'book-sticky-audio',
  'book-word-help',
  ...WORD_HELP_SCOPE_TOUR_TARGETS,
  ...EDGE_STACK_TOUR_TARGETS,
  ...PLACEMENT_PAGE_TOUR_TARGETS,
]);

export type TourTargetResolveMode = 'quick' | 'patient';

export function tourTargetResolveModeForFind(targetKey: string): TourTargetResolveMode {
  return PATIENT_FIND_TOUR_TARGETS.has(targetKey) ? 'patient' : 'quick';
}

export function tourTargetResolveDelayMs(
  targetKey: string,
  mode: TourTargetResolveMode = 'patient'
): number {
  if (mode === 'quick') return TOUR_QUICK_SKIP_MS;
  if (
    targetKey === 'book-sticky-audio' ||
    targetKey === 'book-word-help' ||
    WORD_HELP_SCOPE_TOUR_TARGETS.has(targetKey)
  ) {
    return 900;
  }
  if (EDGE_STACK_TOUR_TARGETS.has(targetKey)) return 1000;
  if (PLACEMENT_PAGE_TOUR_TARGETS.has(targetKey)) return 900;
  return ASYNC_TIP_TOUR_TARGETS.has(targetKey) ? TOUR_TIP_PATIENT_MS : TOUR_TARGET_RESOLVE_MS;
}

/** True when the UI signals this optional target cannot appear (skip without waiting). */
export function canInstantSkipTourTarget(targetKey: string): boolean {
  const bookRoot = document.querySelector('[data-book-tour-capabilities]');
  if (targetKey === 'book-sticky-audio') {
    if (!bookRoot) return false;
    return bookRoot.getAttribute('data-book-has-sticky-audio') !== 'true';
  }
  if (targetKey === 'book-tip') {
    if (!bookRoot) return false;
    return bookRoot.getAttribute('data-book-has-tips') !== 'true';
  }
  if (targetKey === 'lesson-practice') {
    const lessonRoot = document.querySelector('[data-lesson-tour-capabilities]');
    if (!lessonRoot) return false;
    return lessonRoot.getAttribute('data-lesson-has-practice') !== 'true';
  }
  if (targetKey === 'courses-detail-exercises' || targetKey === 'courses-detail-quizzes') {
    const courseRoot = document.querySelector('[data-course-tour-capabilities]');
    if (!courseRoot) return false;
    const attr =
      targetKey === 'courses-detail-exercises'
        ? 'data-course-has-exercises'
        : 'data-course-has-quizzes';
    return courseRoot.getAttribute(attr) !== 'true';
  }
  return false;
}

const TOUR_MIN_HIGHLIGHT_PX = 28;

function isTourTargetElementVisible(el: HTMLElement): boolean {
  if (el.closest('[hidden]')) return false;
  const style = window.getComputedStyle(el);
  if (style.display === 'none' || style.visibility === 'hidden') return false;
  const rect = el.getBoundingClientRect();
  if (rect.width <= 0 && rect.height <= 0) {
    const layoutW = el.offsetWidth;
    const layoutH = el.offsetHeight;
    if (layoutW <= 0 && layoutH <= 0) return false;
  }
  const openPanel = el.closest('[data-settings-panel="open"]');
  if (openPanel instanceof HTMLElement) {
    const panelRect = openPanel.getBoundingClientRect();
    const panelOnScreen =
      panelRect.width > 2 &&
      panelRect.height > 2 &&
      panelRect.bottom > 0 &&
      panelRect.top < window.innerHeight &&
      panelRect.right > 0 &&
      panelRect.left < window.innerWidth;
    const overlapsOpenPanel =
      rect.right > panelRect.left + 1 &&
      rect.left < panelRect.right - 1 &&
      rect.bottom > panelRect.top + 1 &&
      rect.top < panelRect.bottom - 1;
    if (panelOnScreen && overlapsOpenPanel) return true;
  }
  if (isHorizontallyClippedByAncestor(el)) return false;
  return true;
}

export function queryTourTargetElement(pageKey: string, targetKey: string): HTMLElement | null {
  const selector = resolveTourTargetSelector(pageKey, targetKey);
  const nodes = document.querySelectorAll(selector);
  for (const node of nodes) {
    if (node instanceof HTMLElement && isTourTargetElementVisible(node)) return node;
  }
  return null;
}

/** True when an ancestor clips the element off to the side (inactive carousel tab). */
function isHorizontallyClippedByAncestor(el: HTMLElement): boolean {
  const elRect = el.getBoundingClientRect();
  let parent = el.parentElement;
  while (parent && parent !== document.documentElement) {
    const style = window.getComputedStyle(parent);
    const overflowX = style.overflowX;
    if (overflowX === 'hidden' || overflowX === 'clip' || overflowX === 'auto' || overflowX === 'scroll') {
      const parentRect = parent.getBoundingClientRect();
      const intersectsX = elRect.right > parentRect.left + 2 && elRect.left < parentRect.right - 2;
      if (!intersectsX) {
        const overflowY = style.overflowY;
        const scrollsHorizontally = parent.scrollWidth > parent.clientWidth + 1;
        const verticalOnly =
          !scrollsHorizontally &&
          (overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'hidden');
        if (!verticalOnly) return true;
      }
    }
    parent = parent.parentElement;
  }
  return false;
}

/** Part of the element that is actually inside its clipping ancestors and the viewport. */
function visibleTourTargetRect(el: HTMLElement): DOMRect {
  const rect = el.getBoundingClientRect();
  let top = rect.top;
  let left = rect.left;
  let right = rect.right;
  let bottom = rect.bottom;
  let parent = el.parentElement;
  while (parent && parent !== document.documentElement) {
    const style = window.getComputedStyle(parent);
    const overflowX = style.overflowX;
    const overflowY = style.overflowY;
    const clips =
      overflowX === 'hidden' ||
      overflowX === 'clip' ||
      overflowX === 'auto' ||
      overflowX === 'scroll' ||
      overflowY === 'hidden' ||
      overflowY === 'clip' ||
      overflowY === 'auto' ||
      overflowY === 'scroll';
    if (clips) {
      const parentRect = parent.getBoundingClientRect();
      left = Math.max(left, parentRect.left);
      top = Math.max(top, parentRect.top);
      right = Math.min(right, parentRect.right);
      bottom = Math.min(bottom, parentRect.bottom);
    }
    parent = parent.parentElement;
  }
  left = Math.max(left, 0);
  top = Math.max(top, 0);
  right = Math.min(right, window.innerWidth);
  bottom = Math.min(bottom, window.innerHeight);
  return new DOMRect(left, top, Math.max(0, right - left), Math.max(0, bottom - top));
}

/** Highlight rect with padding; expands tiny targets (e.g. tip icons) to a minimum size. */
export function measureTourHighlightRect(el: HTMLElement, pad = 6): {
  top: number;
  left: number;
  width: number;
  height: number;
} {
  const full = el.getBoundingClientRect();
  const visible = visibleTourTargetRect(el);
  const rect = visible.width > 0 && visible.height > 0 ? visible : full;
  const width = Math.max(rect.width + pad * 2, TOUR_MIN_HIGHLIGHT_PX);
  const height = Math.max(rect.height + pad * 2, TOUR_MIN_HIGHLIGHT_PX);
  const cx = rect.left + rect.width / 2;
  const cy = rect.top + rect.height / 2;
  return {
    top: cy - height / 2,
    left: cx - width / 2,
    width,
    height,
  };
}

export function delay(ms: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

/**
 * Focus a target (expanding collapsibles) and return the element if it becomes visible.
 */
export async function resolveTourTargetAfterFocus(
  pageKey: string,
  targetKey: string,
  waitMs = TOUR_TARGET_RESOLVE_MS
): Promise<HTMLElement | null> {
  if (canInstantSkipTourTarget(targetKey)) return null;
  dispatchTourTargetFocus(pageKey, targetKey);
  const immediate = queryTourTargetElement(pageKey, targetKey);
  if (immediate) return immediate;
  const deadline = Date.now() + waitMs;
  while (Date.now() < deadline) {
    const el = queryTourTargetElement(pageKey, targetKey);
    if (el) return el;
    await delay(TARGET_RESOLVE_POLL_MS);
  }
  return queryTourTargetElement(pageKey, targetKey);
}

/**
 * Find the next step index whose target is visible, searching in the given direction.
 */
export async function findResolvableStepIndex(
  pageKey: string,
  steps: ReadonlyArray<{ targetKey: string }>,
  startIndex: number,
  direction: 1 | -1
): Promise<number | null> {
  let i = startIndex;
  while (i >= 0 && i < steps.length) {
    const targetKey = steps[i].targetKey;
    const immediate = queryTourTargetElement(pageKey, targetKey);
    if (immediate) return i;
    if (canInstantSkipTourTarget(targetKey)) {
      i += direction;
      continue;
    }
    const el = await resolveTourTargetAfterFocus(
      pageKey,
      targetKey,
      tourTargetResolveDelayMs(targetKey, tourTargetResolveModeForFind(targetKey))
    );
    if (el) return i;
    i += direction;
  }
  return null;
}
